import { Paper } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import "moment/locale/ru";
import { SnackbarProvider } from "notistack";
import { useEffect, useState } from "react";
import {
	BrowserRouter,
	Navigate,
	Outlet,
	Route,
	Routes,
} from "react-router-dom";
import "./App.css";
import Header from "./components/Header";
import Loader from "./components/Loader";
import { AuthProvider, useAuth } from "./hooks/AuthContext";
import { ManagerAuthResponse } from "./interfaces/Manager";
import AuthPage from "./pages/AuthPage";
import CandidatePage from "./pages/CandidatePage";
import CandidatesPage from "./pages/CandidatesPage";
import InterviewsPage from "./pages/InterviewsPage";
import LetterTemplatePage from "./pages/LetterTemplatePage";
import LetterTemplatesPage from "./pages/LetterTemplatesPage";
import MailingPage from "./pages/MailingPage";
import ManagerPage from "./pages/ManagerPage";
import ManagersPage from "./pages/ManagersPage";
import ProbationPage from "./pages/ProbationPage";
import RegionsPage from "./pages/RegionsPage";
import RequestsPage from "./pages/RequestsPage";
import TasksPage from "./pages/TasksPage";
import TestPage from "./pages/TestPage";
import TestsPage from "./pages/TestsPage";
import UserTestPage from "./pages/UserTest";
import VacanciesPage from "./pages/VacanciesPage";
import VacancyPage from "./pages/VacancyPage";
import { Check } from "./requester/functions";
import { MAIN_URL } from "./requester/requester";

const darkTheme = createTheme({
	palette: {
		mode: "dark",
	},
});

const Lighttheme = createTheme({
	palette: {
		mode: "light",
	},
});

interface LayoutProps {
	isDark: boolean;
	setIsDark(value: boolean): void;
}

const Layout = (props: LayoutProps) => {
	return (
		<>
			<Header isDark={props.isDark} setIsDark={props.setIsDark}>
				<Paper className="p-5 m-5 w-full h-full overflow-auto">
					<Outlet />
				</Paper>
			</Header>
		</>
	);
};

function InnerApp() {
	const [isDark, setIsDark] = useState<boolean>(
		localStorage.getItem("theme") === "dark" ? true : false
	);

	const [auth, setAuth] = useAuth();

	const [isChecked, setIsChecked] = useState<boolean>(false);

	const [locale, setLocale] = useState("ru");

	const onAuth = (data: ManagerAuthResponse) => {
		setAuth({
			id: data.id,
			name: data.full_name,
			role: data.role,
			avatar: `${MAIN_URL}manager/avatar/${data.avatar}`,
		});
	};

	useEffect(() => {
		Check({}, (resp) => {
			setIsChecked(true);
			if (resp.status === "ok") if (resp.data) onAuth(resp.data);
		});
	}, []);

	return (
		<ThemeProvider theme={isDark ? darkTheme : Lighttheme}>
			<LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
				<SnackbarProvider maxSnack={3}>
					<CssBaseline />
					{isChecked ? (
						<BrowserRouter>
							<Routes>
								{auth.role === undefined ? (
									<>
										<Route
											path="/auth"
											element={<AuthPage onAuth={onAuth} />}
										></Route>
										<Route
											path="*"
											element={<Navigate to="/auth" replace={true} />}
										/>
									</>
								) : (
									<Route
										element={<Layout isDark={isDark} setIsDark={setIsDark} />}
									>
										<Route path="/requests" element={<RequestsPage />} />
										<Route path="/managers" element={<ManagersPage />} />
										<Route path="/manager/:id" element={<ManagerPage />} />
										<Route path="/candidate/:id" element={<CandidatePage />} />
										<Route path="/candidates" element={<CandidatesPage />} />
										<Route path="/regions" element={<RegionsPage />} />
										<Route path="/vacancies" element={<VacanciesPage />} />
										<Route path="/vacancy/:id" element={<VacancyPage />} />
										<Route path="/probation/:id" element={<ProbationPage />} />
										<Route path="/tasks" element={<TasksPage />} />
										<Route path="/interviews" element={<InterviewsPage />} />
										<Route path="/letters" element={<MailingPage />} />
										<Route
											path="/templates"
											element={<LetterTemplatesPage />}
										/>
										<Route
											path="/template/:id"
											element={<LetterTemplatePage />}
										/>
										<Route path="/tests" element={<TestsPage />} />
										<Route path="/test/:id" element={<TestPage />} />
										<Route
											path="/test/:id/:userId"
											element={<UserTestPage />}
										/>

										<Route
											path="*"
											element={<Navigate to="/requests" replace={true} />}
										/>
									</Route>
								)}
							</Routes>
						</BrowserRouter>
					) : (
						<Loader />
					)}
				</SnackbarProvider>
			</LocalizationProvider>
		</ThemeProvider>
	);
}

function App() {
	return (
		<AuthProvider>
			<InnerApp />
		</AuthProvider>
	);
}

export default App;
